import React, { useState, useEffect, useRef } from "react";
import Icons from "./Icons";
import { Button } from "./ui/button";
import LoadingOverlay from "./LoadingOverlay";
import { openCustomerPortal } from "@/lib/customer-portal-utils";
import { removePaymentMethod } from "@/services/PaymentService";
import type { GameData, SaveCardProps } from "@/interfaces";
import { isPayPalVaultedEnabled } from "@/lib/game-config-utils";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";

interface PaymentSettingsProps {
  gameData: GameData;
  savedCards: SaveCardProps[];
  onBack: () => void;
  onPayPalRemove?: (paymentMethodId: string) => void;
  onRefreshCards?: () => void;
}

const PaymentSettings = ({ 
  gameData, 
  savedCards, 
  onBack, 
  onPayPalRemove,
  onRefreshCards
}: PaymentSettingsProps) => {
  const theme = useStyleTheme(gameData);
  const { track } = useAnalytics();
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] = useState(false);
  const [isRemovingPayPal, setIsRemovingPayPal] = useState(false);
  const hasTrackedImpressionRef = useRef(false);

  // Track settings page impression
  useEffect(() => {
    if (!hasTrackedImpressionRef.current) {
      hasTrackedImpressionRef.current = true;
      try {
        track('impression_settings', {});
      } catch {
        // Ignore tracking errors
      }
    }
  }, [track]);

  const handleBackClick = () => {
    try {
      track('click_in_settings', { cta_name: 'exit' });
    } catch {
      // Ignore tracking errors
    }
    onBack();
  };

  const handleManageCardsClick = async () => {
    try {
      track('click_in_settings', { cta_name: 'add payment method' });
    } catch {
      // Ignore tracking errors
    }
    await openCustomerPortal({
      gameData,
      onLoadingStart: () => setIsCustomerPortalLoading(true),
    });
  };

  const handlePayPalRemove = async (paymentMethodId: string) => {
    try {
      setIsRemovingPayPal(true);
      
      if (onPayPalRemove) {
        // Use custom handler if provided
        onPayPalRemove(paymentMethodId);
      } else {
        // Default behavior: call API directly
        await removePaymentMethod(paymentMethodId);
        
        // Refresh the cards list
        if (onRefreshCards) {
          onRefreshCards();
        }
      }
    } catch (error) {
      console.error('Error removing PayPal payment method:', error);
      // You could add a toast notification here
    } finally {
      setIsRemovingPayPal(false);
    }
  };

  // Filter PayPal methods if PayPal vaulted is enabled
  const paypalMethods = isPayPalVaultedEnabled(gameData.game_id) 
    ? savedCards.filter(card => card.id.startsWith('paypal_vaulted_'))
    : [];

  const hasPayPalMethod = paypalMethods.length > 0;
  const paypalMethod = hasPayPalMethod ? paypalMethods[0] : null;

  return (
    <>
      <LoadingOverlay 
        isVisible={isCustomerPortalLoading} 
        message="Opening payment settings..." 
        isAbsolute={false}
        theme={theme}
      />
      
      <div className="flex flex-col h-full">
        {/* Header with back button */}
        <div className="flex items-center mb-10 px-5 pt-3 gap-1">
          <Button
            className="w-auto h-auto p-0 mt-1"
            variant="back"
            onClick={handleBackClick}
            aria-label="Back to checkout"
          >
            <Icons type="chevronLeft" size={20}/>
          </Button>
          <span className="text-[#979797] font-['Poppins'] text-[15px] font-normal">
            Back to Checkout
          </span>
        </div>

        {/* Settings content */}
        <div className="flex-1 px-5">
          {/* Info text */}
          <div className="flex items-center mb-6">
            <Icons type="info" size={17} />
            <span className="ml-2 text-[#A2998B] font-['Poppins'] text-[14px] font-normal">
              View / Delete saved payment methods
            </span>
          </div>

          {/* PayPal section */}
          {hasPayPalMethod && paypalMethod && (
            <div className=" -mx-6">
              {/* Top horizontal line - extends further */}
              <div className="h-[1px] bg-[#E5E5E5] w-full mb-0" />
              
              <div className="flex items-center justify-between bg-white px-6 py-4 min-h-[72px]">
                <div className="flex items-center">
                  <div className="w-[46px] h-[29px] mr-3 flex items-center justify-center">
                    <Icons type="card" size={46} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#212121] font-['Poppins'] text-[14px] font-medium">
                      PayPal
                    </span>
                    <span className="text-[#A2998B] font-['Poppins'] text-[12px] font-normal">
                      {(() => {
                        const displayName = paypalMethod.display_name?.replace('PayPal - ', '') || '****';
                        // Extract name before @ sign if it's an email
                        const nameBeforeAt = displayName.includes('@') ? displayName.split('@')[0] : displayName;
                        return `${nameBeforeAt}`;
                      })()}
                    </span>
                  </div>
                </div>
                
                <Button
                  className="w-[32px] h-[32px] p-0 bg-[#F5F5F5] hover:bg-[#E5E5E5] rounded-full flex items-center justify-center"
                  onClick={() => handlePayPalRemove(paypalMethod.id)}
                  disabled={isRemovingPayPal}
                  aria-label="Remove PayPal account"
                >
                  {isRemovingPayPal ? (
                    <div className="w-4 h-4 border-2 border-[#979797] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icons type="close" size={16} />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Single divider between sections */}
          <div className="-mx-6">
            <div className="h-[1px] bg-[#E5E5E5] w-full" />
          </div>

          {/* Manage all cards section */}
          <div className="mb-4 -mx-6">
            <Button 
              className="w-full flex items-center justify-between bg-white px-6 py-4 min-h-[72px] hover:bg-[#F9F9F9] rounded-none"
              onClick={handleManageCardsClick}
            >
              <div className="flex items-center">
                <div className="w-[46px] h-[29px] mr-3 flex items-center justify-center">
                  <Icons type="card" size={46} />
                </div>
                <span className="text-[#212121] font-['Poppins'] text-[14px] font-medium">
                  Manage all cards
                </span>
              </div>
              <Icons type="chevronRight" size={20} />
            </Button>
            
            {/* Bottom horizontal line - extends further */}
            <div className="h-[1px] bg-[#E5E5E5] w-full mt-0" />
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(PaymentSettings);
